<script setup lang="ts">
import ProfilesSelectList from '@/components/shared/ProfilesSelectList.vue';
import { useDepartmentStore } from '@/stores/department';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import TitleBlock from '@/components/shared/blocks/TitleBlock.vue';
import i18n from '@/utils/i18n';
import { IDepartment, IProfile } from '@/interfaces';
import { ref } from 'vue';
import {
  DepartmentFragment,
  MealParticipantFragment,
} from '@/graphql/custom-types';

const departmentStore = useDepartmentStore();
const selectedProfiles = ref<IProfile[]>([]);

const emit = defineEmits([
  'close',
  'profile-selected',
  'confirm',
  'profiles-selected',
]);

interface Props {
  employeeOnly?: boolean;
  selectedProfileType?: 'RESIDENT' | 'EMPLOYEE';
  sharedDepartments?: (IDepartment | DepartmentFragment)[];
  participating?: boolean;
  participants?: (IProfile | MealParticipantFragment)[];
  multiSelect?: boolean;
  title?: string;
  squareTiles?: boolean;
  subTitle?: string;
  textAlignment?: 'left' | 'center' | 'right';
  confirmButtonText?: string;
}

withDefaults(defineProps<Props>(), {
  employeeOnly: false,
  selectedProfileType: 'RESIDENT',
  sharedDepartments: () => [],
  participants: () => [],
  multiSelect: false,
  title: i18n.global.t('profileSelect.header'),
  textAlignment: 'left',
});

async function profileSelected(profile: IProfile) {
  emit('profile-selected', profile);
}

async function profilesSelected(profiles: IProfile[]) {
  selectedProfiles.value = profiles;
  emit('profiles-selected', profiles);
}
</script>

<template>
  <BaseOverlay @close="emit('close')">
    <template #content>
      <div class="profile-select h-max bg-neutral-100">
        <div class="h-max flex flex-col">
          <TitleBlock :text="title" :class="`text-${textAlignment}`" />
          <p class="b500" :class="`text-${textAlignment}`">{{ subTitle }}</p>

          <ProfilesSelectList
            class="h-full mb-12"
            :employee-only="employeeOnly"
            :selected-profile-type="selectedProfileType"
            :shared-departments="sharedDepartments"
            :participating="participating"
            :participants="participants"
            :selectedDepartment="departmentStore"
            :multiselect="multiSelect"
            :square-tiles="squareTiles"
            :confirm-button-text="confirmButtonText"
            @profile-selected="profileSelected"
            @profiles-selected="profilesSelected"
            @save="profilesSelected"
            @close="emit('close')"
            @confirm="emit('confirm')"
          />
        </div>
      </div>
    </template>
  </BaseOverlay>
</template>
